// =====================================================
// HOOK: useCallStats
// Description: Hook pour récupérer les statistiques d'appels par utilisateur
// =====================================================

import { useState, useEffect, useCallback } from 'react';
import { useUsers } from '../contexts/UsersContext';
import { useSupabase } from './useSupabase';
import { CREW_STATUS, type Crew } from '@/types/crew';
import { monitorQuery } from '@/lib/supabase-monitor';
import { getStartDateForPeriod, type PeriodValue } from '@/components/ui';
import type { CallDirection, CallOutcome } from '@/types/timeline';
import { excludeArchived } from '@/lib/activeCrews';

export interface CallStatsByUser {
  user_id: string;
  user_name: string;
  crews_count: number;
  answered_calls: number;
  no_answer_calls: number;
  voicemail_calls: number;
}

/**
 * Interface pour un appel détaillé
 */
export interface CallDetail {
  id: string;
  created_at: string;
  created_by: string;
  entity_type: string;
  entity_id: string;
  title: string;
  description: string | null;
  metadata: {
    direction: CallDirection;
    outcome: CallOutcome;
    duration?: number;
    notes?: string;
    entity_name?: string;
  };
  /** Nom résolu de l'entité (enrichi côté client) */
  resolved_entity_name?: string;
}

interface UseCallStatsOptions {
  /** Période de filtrage des appels */
  period?: PeriodValue;
}

/**
 * Hook pour récupérer les statistiques d'appels par utilisateur
 * Compte les appels répondus et sans réponse pour chaque utilisateur
 *
 * @param crews - Optionnel: passer les crews déjà chargés pour éviter une requête
 * @param options - Options de configuration
 */
export function useCallStats(crews?: Crew[], options: UseCallStatsOptions = {}) {
  const { period = '30d' } = options;
  const { users, loading: loadingUsers } = useUsers();
  const supabase = useSupabase();
  const [callStats, setCallStats] = useState<CallStatsByUser[]>([]);
  const [allCalls, setAllCalls] = useState<CallDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [localCrews, setLocalCrews] = useState<Crew[]>([]);

  // Charger les crews uniquement si non fournis
  useEffect(() => {
    if (crews) {
      // CRM-12 : exclure les archivés même quand les crews sont passés en prop
      setLocalCrews(excludeArchived(crews));
      return;
    }

    // Sinon charger manuellement
    async function fetchCrews() {
      const { data, error } = await monitorQuery(
        'crews',
        'select',
        'for call stats',
        // CRM-12 : exclure les archivés dès la requête
        (() =>
          supabase
            .from('crews')
            .select('id, assigned_to')
            .neq('status', CREW_STATUS.ARCHIVED)) as any,
        'useCallStats.fetchCrews'
      );

      if (!error && data) {
        setLocalCrews(data as Crew[]);
      }
    }

    fetchCrews();
  }, [crews, supabase]);

  const fetchCallStats = useCallback(async () => {
    if (users.length === 0 || loadingUsers || localCrews.length === 0) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // Calculer la date de début selon la période
      const startDate = getStartDateForPeriod(period);

      // ✅ OPTIMISATION : Une seule requête pour TOUS les appels au lieu d'une par utilisateur
      let query = supabase
        .from('timeline_events')
        .select('id, created_at, created_by, entity_type, entity_id, title, description, metadata')
        .eq('event_type', 'call_logged')
        .order('created_at', { ascending: false });

      // Appliquer le filtre de période si défini
      if (startDate) {
        query = query.gte('created_at', startDate.toISOString());
      }

      const { data: callsData, error } = await monitorQuery(
        'timeline_events',
        'select',
        `all call_logged events for stats (period: ${period})`,
        (() => query) as any,
        'useCallStats.fetchAllCalls'
      );

      if (error) throw error;

      // S'assurer que callsData est un tableau
      const calls = Array.isArray(callsData) ? (callsData as CallDetail[]) : [];

      // ✅ Enrichir les appels avec les noms résolus des entités
      // Extraire les IDs uniques par type d'entité
      const crewIds = [
        ...new Set(calls.filter((c) => c.entity_type === 'crew').map((c) => c.entity_id)),
      ];
      const contactIds = [
        ...new Set(calls.filter((c) => c.entity_type === 'contact').map((c) => c.entity_id)),
      ];

      // Charger les noms des crews
      let crewsMap: Record<string, string> = {};
      if (crewIds.length > 0) {
        const { data: crewsData } = await supabase
          .from('crews')
          .select('id, name')
          .in('id', crewIds);
        if (crewsData) {
          crewsMap = Object.fromEntries(crewsData.map((c: any) => [c.id, c.name]));
        }
      }

      // Charger les noms des contacts
      let contactsMap: Record<string, string> = {};
      if (contactIds.length > 0) {
        const { data: contactsData } = await supabase
          .from('contacts')
          .select('id, first_name, last_name')
          .in('id', contactIds);
        if (contactsData) {
          contactsMap = Object.fromEntries(
            contactsData.map((c: any) => [
              c.id,
              `${c.first_name || ''} ${c.last_name || ''}`.trim(),
            ])
          );
        }
      }

      // Enrichir les appels avec les noms résolus
      const enrichedCalls = calls.map((call) => {
        let resolvedName = call.metadata?.entity_name;

        if (!resolvedName) {
          if (call.entity_type === 'crew') {
            resolvedName = crewsMap[call.entity_id];
          } else if (call.entity_type === 'contact') {
            resolvedName = contactsMap[call.entity_id];
          }
        }

        return {
          ...call,
          resolved_entity_name: resolvedName || undefined,
        };
      });

      setAllCalls(enrichedCalls);

      // Compter les statistiques côté client (beaucoup plus rapide)
      const stats = users.map((user) => {
        // Compter équipages assignés
        const assignedCrews = localCrews.filter((c) => c.assigned_to === user.id);

        // Filtrer les appels de cet utilisateur
        const userCalls = calls.filter((call: any) => call.created_by === user.id);

        // Compter les appels répondus
        const answeredCalls = userCalls.filter(
          (call: any) => call.metadata?.outcome === 'answered'
        ).length;

        // Compter les appels sans réponse
        const noAnswerCalls = userCalls.filter(
          (call: any) => call.metadata?.outcome === 'no_answer'
        ).length;

        // Compter les répondeurs
        const voicemailCalls = userCalls.filter(
          (call: any) => call.metadata?.outcome === 'voicemail'
        ).length;

        return {
          user_id: user.id,
          user_name: user.full_name || user.email,
          crews_count: assignedCrews.length,
          answered_calls: answeredCalls,
          no_answer_calls: noAnswerCalls,
          voicemail_calls: voicemailCalls,
        };
      });

      setCallStats(stats);
    } catch (error) {
      console.error("Erreur lors du chargement des stats d'appels:", error);
      setCallStats([]);
    } finally {
      setLoading(false);
    }
  }, [users, localCrews, supabase, loadingUsers, period]);

  useEffect(() => {
    fetchCallStats();
  }, [fetchCallStats]);

  /**
   * Récupère les appels détaillés d'un utilisateur spécifique
   */
  const getCallsByUserId = useCallback(
    (userId: string): CallDetail[] => {
      return allCalls.filter((call) => call.created_by === userId);
    },
    [allCalls]
  );

  return { callStats, loading, allCalls, getCallsByUserId };
}
